import { Directive, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, AsyncValidator, NG_ASYNC_VALIDATORS, ValidationErrors } from '@angular/forms';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { SurveyJob } from 'src/app/shared/survey-data-services/survey-job';
import { SurveyJobService } from 'src/app/shared/survey-data-services/survey-job.services';

@Directive({
    selector: '[surveyJobNameValidator]',
    providers: [{ provide: NG_ASYNC_VALIDATORS, useExisting: SurveyJobNameValidatorDirective, multi: true }]
})
export class SurveyJobNameValidatorDirective implements AsyncValidator, OnChanges {
    @Input()
    surveyJobName: string;

    @Input()
    checkDuplicateOnDirty = true;

    private _onValidatorChange = () => {};

    constructor(private surveyJobService: SurveyJobService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.surveyJobName) {
            this._onValidatorChange();
        }
    }

    registerOnValidatorChange?(fn: () => void): void {
        this._onValidatorChange = fn;
    }

    public hasSpecialCharacters(fileName: string): boolean {
        let specialChars = '?/<>*":\\@^+[]$|';
        return [...specialChars].some(char => fileName.includes(char));
    }

    private validateSync(control: AbstractControl): ValidationErrors | null {
        const name = control.value as string;
        let nameWithoutWhitespaces = '';

        if (name) {
            if (name.trim().startsWith('.')) {
                return { invalidName: true };
            } else if (name.trim().endsWith('.')) {
                return { invalidName: true };
            } else if (name.length > 32) {
                return { exceedsMaxLength: true };
            } else if (this.hasSpecialCharacters(name)) {
                return { specialChars: true };
            } else {
                // shouldn't consider just whitespaces to be a valid name
                nameWithoutWhitespaces = name.replace(/\s+/g, '');
                if (!nameWithoutWhitespaces) {
                    return { required: true };
                } else {
                    return null;
                }
            }
        } else {
            return { required: true };
        }
    }

    validate(control: AbstractControl): Observable<ValidationErrors | null> {
        const name = control.value as string;

        // First, perform synchronous validation
        const syncErrors = this.validateSync(control);
        if (syncErrors) {
            return of(syncErrors);
        }

        // Only check for duplicates if the basic validation passes and field is dirty (or checkDuplicateOnDirty is false)
        if (!name || (this.checkDuplicateOnDirty && !control.dirty)) {
            return of(null);
        }

        // Debounce the API call to avoid too many requests while typing
        return timer(300).pipe(
            switchMap(() => {
                const trimmedName = name.trim();
                // Use filter to check if a job with this exact name exists
                return this.surveyJobService.getSurveyJobs({ filter: `$Name eq '${trimmedName}'` });
            }),
            map((response: { items: SurveyJob[]; total: number; statusCode?: number }) => {
                // If any job with this name exists, return notUnique error
                if (response.items && response.items.length > 0) {
                    return { notUnique: true };
                }
                return null;
            }),
            catchError(() => {
                // On error, don't block the form
                return of(null);
            })
        );
    }
}
